import React, { useEffect, useRef, useState } from "react";
import { FaCheck, FaStar } from "react-icons/fa";
import "./RatingFlowDemo.css";

const SHOP_IMAGE = "/images/hair.jpg";

const COPY = {
  en: {
    rateLabel: "Rate your visit",
    reviewLabel: "Share feedback",
    shop: "Curly Hair Studio",
    service: "Haircut & blow-dry",
    shopImage: SHOP_IMAGE,
    shopImageAlt: "Curly Hair Studio salon",
    review: "Amazing service — will book again!",
    posted: "Review posted!",
    thanks: "Thanks for helping others discover great shops",
  },
  ar: {
    rateLabel: "قيّم زيارتك",
    reviewLabel: "شارك رأيك",
    shop: "صالون كيرلي",
    service: "قص شعر وتسريح",
    shopImage: SHOP_IMAGE,
    shopImageAlt: "صالون كيرلي",
    review: "خدمة رائعة — سأحجز مرة أخرى!",
    posted: "تم نشر التقييم!",
    thanks: "شكراً لمساعدة الآخرين في اكتشاف محلات مميزة",
  },
};

export default function RatingFlowDemo({ lang = "en", featured = false }) {
  const rootRef = useRef(null);
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | stars | review | posted
  const [starCount, setStarCount] = useState(0);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!active) {
      setPhase("idle");
      setStarCount(0);
      return undefined;
    }

    let cancelled = false;
    const timers = [];

    const run = () => {
      if (cancelled) return;
      setPhase("idle");
      setStarCount(0);

      timers.push(setTimeout(() => !cancelled && setPhase("stars"), 500));

      for (let i = 1; i <= 5; i += 1) {
        timers.push(
          setTimeout(() => !cancelled && setStarCount(i), 900 + i * 280)
        );
      }

      timers.push(setTimeout(() => !cancelled && setPhase("review"), 2600));
      timers.push(setTimeout(() => !cancelled && setPhase("posted"), 4200));
      timers.push(setTimeout(() => !cancelled && run(), 6800));
    };

    run();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [active]);

  const copy = COPY[lang] || COPY.en;
  const showReview = phase === "review" || phase === "posted";
  const showPosted = phase === "posted";

  return (
    <div
      ref={rootRef}
      className={`rfd-demo${featured ? " rfd-demo--featured" : ""}${active ? " rfd-demo--active" : ""}${showPosted ? " rfd-demo--posted" : ""}`}
      data-phase={phase}
      aria-hidden="true"
    >
      <div className="rfd-stage">
        <div className="rfd-panel rfd-panel--rate">
          <div className="rfd-panel-head">
            <p className="rfd-panel-label">{copy.rateLabel}</p>
          </div>
          <div className="rfd-panel-body">
            <div className="rfd-account">
              <img
                className="rfd-account-avatar"
                src={copy.shopImage}
                alt={copy.shopImageAlt}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = "/images/shop-placeholder.png";
                }}
              />
              <div className="rfd-account-info">
                <strong className="rfd-account-name">{copy.shop}</strong>
                <span className="rfd-account-meta">{copy.service}</span>
              </div>
            </div>
            <div className="rfd-stars" role="presentation">
              {[1, 2, 3, 4, 5].map((n) => (
                <FaStar
                  key={n}
                  className={[
                    "rfd-star",
                    starCount >= n ? "rfd-star--filled" : "",
                    phase === "stars" && starCount + 1 === n ? "rfd-star--pulse" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
              ))}
            </div>
            <div className={`rfd-review${showReview ? " rfd-review--show" : ""}`}>
              <p className="rfd-panel-label rfd-panel-label--inline">{copy.reviewLabel}</p>
              <p className="rfd-review-text">{copy.review}</p>
            </div>
          </div>
        </div>

        <div className={`rfd-confirm${showPosted ? " rfd-confirm--show" : ""}`}>
          <span className="rfd-confirm-icon">
            <FaCheck aria-hidden="true" />
          </span>
          <strong className="rfd-confirm-title">{copy.posted}</strong>
          <span className="rfd-confirm-summary">{copy.thanks}</span>
        </div>
      </div>
    </div>
  );
}
